/* ============================================================
 * <lion-quick-add-button>
 * - Single-variant products: direct AJAX add to cart.
 * - Multi-variant products: open <lion-quick-add-modal> and fetch
 *   the quick-add section for the product.
 *
 * <lion-quick-add-modal>
 * - Single instance in theme.liquid. Receives injected HTML,
 *   handles open/close, focus trap, ESC/overlay dismiss.
 * ============================================================ */

class QuickAddButton extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-quick-add-trigger]');
    this.productUrl = this.dataset.productUrl;
    this.variantId = this.dataset.variantId;
    this.hasOnlyDefault = this.dataset.hasOnlyDefaultVariant === 'true';
    this.trigger?.addEventListener('click', (e) => this.onClick(e));
  }

  async onClick(event) {
    event.preventDefault();
    if (this.hasOnlyDefault) {
      await this.directAdd();
    } else {
      await this.openModal();
    }
  }

  setLoading(isLoading) {
    if (!this.trigger) return;
    this.trigger.disabled = isLoading;
    this.trigger.classList.toggle('loading', isLoading);
  }

  async directAdd() {
    this.setLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');
      const cartType = document.querySelector('lion-cart-drawer') ? 'drawer' : 'page';
      if (cartType === 'drawer') {
        formData.append('sections', 'cart-drawer');
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(window.shopUrls.cart_add, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.description || data.message || res.statusText);
      }
      window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'quick-add',
        productVariantId: this.variantId,
        cartData: data,
      });
    } catch (err) {
      console.error('[quick-add] direct add failed:', err);
      window.publish?.(window.PUB_SUB_EVENTS.cartError, { message: err.message });
    } finally {
      this.setLoading(false);
    }
  }

  async openModal() {
    const modal = document.querySelector('lion-quick-add-modal');
    if (!modal) {
      window.location.href = this.productUrl;
      return;
    }
    this.setLoading(true);
    try {
      const url = `${this.productUrl}?section_id=quick-add`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      modal.setContent(html);
      modal.open(this.trigger);
    } catch (err) {
      console.error('[quick-add] modal fetch failed:', err);
      window.location.href = this.productUrl;
    } finally {
      this.setLoading(false);
    }
  }
}

class QuickAddModal extends HTMLElement {
  constructor() {
    super();
    this.contentEl = this.querySelector('[data-quick-add-content]');
    this.dialog = this.querySelector('.quick-add-modal__dialog');
    this.previousFocus = null;
    this.onKeydown = this.onKeydown.bind(this);

    this.querySelectorAll('[data-quick-add-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    // Close after successful add to cart.
    window.subscribe?.(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (payload?.source === 'lion-quick-add-modal') this.close();
    });
  }

  setContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const section = doc.querySelector('.quick-add');
    this.contentEl.innerHTML = '';
    if (section) {
      this.contentEl.appendChild(section);
    } else {
      this.contentEl.innerHTML = html;
    }
    // Tag lion-product-form submissions so modal can auto-close.
    this.contentEl.querySelectorAll('lion-product-form').forEach((pf) => {
      pf.dataset.source = 'lion-quick-add-modal';
    });
  }

  open(returnFocusTo) {
    this.previousFocus = returnFocusTo || document.activeElement;
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.setAttribute('open', ''));
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);
    const firstFocusable = this.dialog?.querySelector(
      'button, [href], input:not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }

  close() {
    this.removeAttribute('open');
    setTimeout(() => {
      this.setAttribute('hidden', '');
      if (this.contentEl) this.contentEl.innerHTML = '';
    }, 240);
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);
    this.previousFocus?.focus?.();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusables = this.dialog?.querySelectorAll(
      'button:not([disabled]), [href], input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables || !focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('lion-quick-add-button')) {
  customElements.define('lion-quick-add-button', QuickAddButton);
}
if (!customElements.get('lion-quick-add-modal')) {
  customElements.define('lion-quick-add-modal', QuickAddModal);
}



/* ------------------------------------------------------------
 * Card product quick view popup
 * Turns the "Visualizza" button inside product cards into a real
 * mini product-page popup. It fetches sections/quick-add.liquid
 * through Shopify Section Rendering API and injects it into the
 * existing <lion-quick-add-modal> shell.
 * ------------------------------------------------------------ */
(function () {
  if (window.__lionCardQuickViewBound) return;
  window.__lionCardQuickViewBound = true;

  function getProductUrl(trigger) {
    const rawUrl = trigger?.dataset?.productUrl || trigger?.getAttribute('href');
    if (!rawUrl || rawUrl === '#') return '';
    try {
      return new URL(rawUrl, window.location.origin).pathname;
    } catch (error) {
      return rawUrl;
    }
  }

  document.addEventListener('click', async function (event) {
    const trigger = event.target.closest('[data-card-quick-view]');
    if (!trigger) return;

    const productUrl = getProductUrl(trigger);
    if (!productUrl) return;

    const modal = document.querySelector('lion-quick-add-modal');
    if (!modal || !window.fetch) return;

    event.preventDefault();
    event.stopPropagation();

    trigger.setAttribute('aria-busy', 'true');
    trigger.classList.add('loading');

    try {
      const separator = productUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${productUrl}${separator}section_id=quick-add`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(response.statusText || 'Errore caricamento prodotto');

      const html = await response.text();
      modal.setContent(html);
      modal.open(trigger);
    } catch (error) {
      console.error('[card-product-quick-view] failed:', error);
      window.location.href = productUrl;
    } finally {
      trigger.removeAttribute('aria-busy');
      trigger.classList.remove('loading');
    }
  }, true);
})();

/* ------------------------------------------------------------
 * Card product native quick add fallback
 * Uses a real Shopify /cart/add form, then enhances it with AJAX.
 * This keeps card buttons clickable even if the quick-add custom element
 * is not the click target or a product has no quick-add modal available.
 * ------------------------------------------------------------ */
(function () {
  if (window.__lionCardQuickAddFormBound) return;
  window.__lionCardQuickAddFormBound = true;

  document.addEventListener('submit', async function (event) {
    const form = event.target.closest('[data-card-quick-add-form]');
    if (!form) return;

    const submitButton = form.querySelector('[type="submit"]');
    const variantInput = form.querySelector('[name="id"]');
    const cartAddUrl = window.shopUrls?.cart_add || form.getAttribute('action') || '/cart/add';

    if (!variantInput?.value || !window.fetch) return;

    event.preventDefault();
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');

    const formData = new FormData(form);
    if (!formData.has('quantity')) formData.append('quantity', '1');

    const hasDrawer = !!document.querySelector('lion-cart-drawer');
    if (hasDrawer) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || response.statusText || 'Errore aggiunta carrello');
      }

      window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update', {
        source: 'card-product-quick-add',
        productVariantId: formData.get('id'),
        cartData: data
      });
    } catch (error) {
      console.error('[card-product-quick-add] failed:', error);
      window.publish?.(window.PUB_SUB_EVENTS?.cartError || 'cart-error', {
        source: 'card-product-quick-add',
        message: error.message
      });
    } finally {
      submitButton?.removeAttribute('aria-disabled');
      submitButton?.classList.remove('loading');
    }
  });
})();
